import { useEffect, useRef, useState } from "react"

const DEFAULT_SPEED_MS = 14
// Backlog at which we start flushing >1 char per tick so the typewriter
// can keep up with bursty streaming (large reasoning chunks, replayed
// reports, etc.). Below the threshold the original 1 char / 14ms cadence
// is preserved so short bursts still feel like a typewriter.
const BACKLOG_FLUSH_THRESHOLD = 80
// Once the backlog passes the threshold, advance proportional to its
// size so we converge on the target. Empirically tuned: at 800 chars
// behind we flush ~32 chars/tick (2.3 kchars/sec at 14ms cadence), which
// catches up most reasoning chunks within a few hundred ms while still
// reading like animated text rather than a paste.
const BACKLOG_FLUSH_DIVISOR = 25
// Hard cap so we never display more than ~200 chars of pre-typed buffer
// at once — keeps the leading edge close to the live cursor when we're
// catching up from a very large backlog (multi-thousand char chunks).
const MAX_BACKLOG_KEEP = 200

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false
  }
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

/**
 * Reveal `text` one character at a time on mount. Re-runs the reveal whenever
 * `replayKey` changes — pass a stable id (e.g. lane-entry id) so each
 * entry types itself once. When `text` grows (live streaming), the typewriter
 * advances toward the new target without restarting from scratch — every
 * character that arrives is typed in order, even mid-stream.
 *
 * Adaptive catch-up: when the backlog (target length minus revealed
 * length) exceeds BACKLOG_FLUSH_THRESHOLD, the tick advances by more than
 * one character so the display stays near the live cursor. A bursty 1000-
 * char reasoning chunk would otherwise need 14s to fully render and
 * could trail the backend by several tool calls; with adaptive flush it
 * catches up in a few hundred ms while still animating rather than
 * popping in.
 *
 * When the user has `prefers-reduced-motion: reduce` set, the full text is
 * returned immediately and no timer runs.
 *
 * `instant=true` short-circuits the animation: the full text is displayed
 * immediately and the timer never runs. Pass it when the text is already
 * finished (e.g. an expanded lane reasoning block whose `live` flag is
 * false, a finished tool call, a static status line) so users don't see
 * old content character-by-character every time the lane is reopened.
 * The `replayKey` reset still applies — flipping `instant` from true →
 * false (e.g. a block transitioning from live to done) leaves the full
 * text displayed; the reverse (which shouldn't happen in practice) would
 * still need a `replayKey` change to restart animation.
 */
// eslint-disable-next-line max-params -- 4 independent typewriter knobs; an options object would just be boilerplate at call sites
export function useTypewriter(
  text: string,
  replayKey: string,
  speedMs: number = DEFAULT_SPEED_MS,
  instant: boolean = false
) {
  const shouldSkipAnimation = instant || prefersReducedMotion()
  const [displayed, setDisplayed] = useState<string>(() => (shouldSkipAnimation ? text : ""))
  const targetRef = useRef<string>("")
  const indexRef = useRef<number>(0)
  const timeoutRef = useRef<number | null>(null)

  // Reset index whenever the replayKey changes — that's the signal a new
  // entry is being typed (different lane row, live → done transition, etc.).
  useEffect(() => {
    if (shouldSkipAnimation) {
      setDisplayed(targetRef.current)
      return
    }
    indexRef.current = 0
    setDisplayed("")
  }, [replayKey, shouldSkipAnimation])

  useEffect(() => {
    targetRef.current = text
    if (shouldSkipAnimation || !text) {
      indexRef.current = text.length
      setDisplayed(text)
      return
    }
    if (timeoutRef.current != null) return
    const tick = () => {
      timeoutRef.current = null
      const target = targetRef.current
      const remaining = target.length - indexRef.current
      if (remaining <= 0) return
      // Adaptive step size: 1 char while caught up, scales with backlog
      // once we're behind. The hard floor of `remaining - MAX_BACKLOG_KEEP`
      // ensures very large backlogs (5k+ chars) don't take forever.
      let step = 1
      if (remaining > BACKLOG_FLUSH_THRESHOLD) {
        step = Math.max(step, Math.floor(remaining / BACKLOG_FLUSH_DIVISOR))
        if (remaining > MAX_BACKLOG_KEEP) {
          step = Math.max(step, remaining - MAX_BACKLOG_KEEP)
        }
      }
      indexRef.current = Math.min(target.length, indexRef.current + step)
      setDisplayed(target.slice(0, indexRef.current))
      if (indexRef.current < target.length) {
        timeoutRef.current = window.setTimeout(tick, speedMs)
      }
    }
    timeoutRef.current = window.setTimeout(tick, speedMs)
    return () => {
      if (timeoutRef.current != null) {
        window.clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
    // QA #SP3: include `replayKey` so a replay with the same `text` (e.g. a
    // subagent retrying the identical web search) restarts the timer that
    // Effect 1 just reset. Without this dep, displayed stays "" forever
    // because Effect 2's deps are unchanged.
  }, [replayKey, text, speedMs, shouldSkipAnimation])

  return displayed
}
